/**
 * Invoice Refund Service - Frontend service for refund operations
 */

import api from "../../../shared/api.jsx";
import { logInfo, logError } from "../../../shared/utils/logger.js";

/**
 * Create a refund for an invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} refundData - Refund data
 * @param {number} refundData.amount - Refund amount
 * @param {string} refundData.reason - Reason for refund
 * @param {string} refundData.method - Refund method
 * @param {string} refundData.reference - Refund reference
 * @param {string} refundData.notes - Additional notes
 * @returns {Promise<Object>} Creation result
 */
export const createRefund = async (invoiceId, refundData) => {
  try {
    const response = await api.post(`/invoices/${invoiceId}/refunds`, refundData);
    logInfo("Refund created successfully", { invoiceId });
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Refund created successfully",
    };
  } catch (error) {
    logError("Failed to create refund", {
      invoiceId,
      error: error.message,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to create refund",
    };
  }
};

/**
 * Process a refund (update status)
 * @param {string} invoiceId - Invoice ID
 * @param {number} refundId - Refund index
 * @param {Object} updateData - Update data
 * @param {string} updateData.status - New status
 * @param {string} updateData.notes - Additional notes
 * @returns {Promise<Object>} Update result
 */
export const processRefund = async (invoiceId, refundId, updateData) => {
  try {
    const response = await api.patch(
      `/invoices/${invoiceId}/refunds/${refundId}`,
      updateData
    );
    logInfo("Refund processed successfully", { invoiceId, refundId });
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Refund processed successfully",
    };
  } catch (error) {
    logError("Failed to process refund", {
      invoiceId,
      refundId,
      error: error.message,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to process refund",
    };
  }
};

/**
 * Preview refund confirmation email
 * @param {string} invoiceId - Invoice ID
 * @param {number} refundId - Refund index
 * @returns {Promise<Object>} Preview result
 */
export const previewRefundConfirmationEmail = async (invoiceId, refundId) => {
  try {
    const response = await api.get(
      `/invoices/${invoiceId}/refunds/${refundId}/preview-email`
    );
    // The API returns { success: true, data: { emailContent, whatsappContent, ... } }
    // response is already the parsed JSON, so response.data contains the actual data
    return {
      success: response.success !== false,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to preview email", {
      invoiceId,
      refundId,
      error: error.message,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to preview email",
    };
  }
};

/**
 * Send refund confirmation pack
 * @param {string} invoiceId - Invoice ID
 * @param {number} refundId - Refund index
 * @param {Object} options - Send options
 * @param {string} options.emailContent - Custom email content
 * @param {Array<string>} options.cc - CC emails
 * @param {Array<string>} options.bcc - BCC emails
 * @param {boolean} options.sendEmail - Send email
 * @param {boolean} options.sendWhatsApp - Send WhatsApp
 * @returns {Promise<Object>} Send result
 */
export const sendRefundConfirmation = async (invoiceId, refundId, options = {}) => {
  try {
    const response = await api.post(
      `/invoices/${invoiceId}/refunds/${refundId}/send-confirmation`,
      options
    );
    logInfo("Refund confirmation sent successfully", { invoiceId, refundId });
    return {
      success: true,
      data: response.data || response,
      message: response.message || "Refund confirmation sent successfully",
    };
  } catch (error) {
    logError("Failed to send confirmation", {
      invoiceId,
      refundId,
      error: error.message,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to send confirmation",
    };
  }
};

